import React, { useState } from 'react';
import { Avatar, Box, Grid, TextField, Link, Paper, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PlaceIcon from '@mui/icons-material/Place';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { redirect } from 'react-router-dom';
import authSlice from '../store/slices/auth';
import { RootState } from '../store';
import { Navigate } from 'react-router';
import { USER_TYPE_ADMIN, USER_TYPE_WAREHOUSE } from '../types/auth/types';
import { loginFailure, LoginFieldFaults } from '../utils/loginErrorMessage';
import PasswordField from '../components/PasswordField/PasswordField';

// The package.json version plus the 7-character commit as SemVer build metadata, e.g. 1.1.0+48b1f83.
// Set by `npm run build`; empty without a version, so the line is omitted.
const buildVersion = process.env.REACT_APP_BUILD_VERSION;

const LOGIN_ERROR_ID = 'login-error';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [alertKey, setAlertKey] = useState(0);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [invalidPassword, setInvalidPassword] = useState(false);
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);

  if (auth.user) {
    // Already authenticated, go to appropriate page based on user type
    const redirectPath =
      auth.user.user_type === USER_TYPE_ADMIN || auth.user.user_type === USER_TYPE_WAREHOUSE
        ? '/home/vehicles'
        : '/home';
    return <Navigate to={redirectPath} />;
  }

  // The key changes on every failed submit so the alert remounts. The same text on the same node is not announced.
  const showFailure = (text: string, fields: LoginFieldFaults) => {
    setAlertKey(key => key + 1);
    setMessage(text);
    setInvalidEmail(fields.email);
    setInvalidPassword(fields.password);
  };

  const handleLogin = (email: string, password: string) => {
    axios
      .post(`${process.env.REACT_APP_API_URL}/auth/login/`, { email, password })
      .then(res => {
        // Store nothing unless the answer is a whole session. The store is persisted to localStorage.
        // Tokens without a user would stay there while the page says sign-in failed (#161).
        // The catch below shows the failure.
        if (!res.data?.access || !res.data?.refresh || !res.data?.user) {
          throw new Error('The sign-in response has no user or no token pair.');
        }
        dispatch(
          authSlice.actions.setAuthTokens({
            token: res.data.access,
            refreshToken: res.data.refresh,
          })
        );
        dispatch(authSlice.actions.setUser(res.data.user));
        setLoading(false);
        const redirectPath =
          res.data.user.user_type === USER_TYPE_ADMIN || res.data.user.user_type === USER_TYPE_WAREHOUSE
            ? '/home/vehicles'
            : '/home';
        redirect(redirectPath);
      })
      // Also catches the guard's throw above (a 200 that is not a whole session): the button never stays
      // "loading". The helper tells such a throw from a request that got no response (#117).
      .catch(err => {
        setLoading(false);
        const failure = loginFailure(err);
        showFailure(failure.message, failure.fields);
      });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const email = `${data.get('email') ?? ''}`;
    const password = `${data.get('password') ?? ''}`;
    // The form is noValidate, so check here: the API's blank-field 400 doesn't say which field.
    if (!email.trim() || !password) {
      showFailure('Enter your email and password.', { email: !email.trim(), password: !password });
      return;
    }
    setMessage('');
    setInvalidEmail(false);
    setInvalidPassword(false);
    setLoading(true);
    handleLogin(email, password);
  };

  return (
    <Grid container component="main" sx={{ height: { xs: '100vw', sm: '100vh' } }}>
      <Grid
        item
        xs={12}
        sm={4}
        md={7}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: { xs: '25vh', sm: '100vh' },
          backgroundImage: 'url(/better_cargo_background.jpg)',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Box
          component="img"
          sx={{
            width: { xs: '60%', sm: '30%' },
            height: { xs: '40%', sm: 'auto' },
            objectFit: 'contain',
            my: 2,
          }}
          alt="logo"
          src="/better_cargo_logo_white.svg"
        />
        <Box
          sx={{
            my: { xs: 1, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            fontSize: { xs: '24px', sm: '30px' },
            gap: 3,
          }}
        >
          <Link href="mailto:info@bettercargo.services" sx={{ color: '#fff' }}>
            <EmailIcon fontSize="inherit" />
          </Link>
          <Link href="tel:+15148875999" sx={{ color: '#fff' }}>
            <PhoneIcon fontSize="inherit" />
          </Link>
          <Link href="tel:+15144317292" sx={{ color: '#fff' }}>
            <PhoneAndroidIcon fontSize="inherit" />
          </Link>
          <Link href="https://goo.gl/maps/JR419JF2Bc8buKiu5" sx={{ color: '#fff' }}>
            <PlaceIcon fontSize="inherit" />
          </Link>
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        sm={8}
        md={5}
        component={Paper}
        elevation={6}
        square
        sx={{ boxShadow: { xs: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' } }}
      >
        <Box
          sx={{
            width: '100%',
            my: 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Sign in
          </Typography>
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ width: '100%', mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              error={invalidEmail}
              inputProps={invalidEmail && message ? { 'aria-describedby': LOGIN_ERROR_ID } : undefined}
            />
            <PasswordField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              id="password"
              autoComplete="current-password"
              error={invalidPassword}
              inputProps={invalidPassword && message ? { 'aria-describedby': LOGIN_ERROR_ID } : undefined}
            />
            {message ? (
              <Typography
                key={alertKey}
                id={LOGIN_ERROR_ID}
                role="alert"
                variant="body2"
                color="primary"
                align="center"
                sx={{ mt: 1 }}
              >
                {message}
              </Typography>
            ) : null}
            {/* <FormControlLabel control={<Checkbox value="remember" color="primary" />} label="Remember me" />*/}
            <LoadingButton loading={loading} type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
              Sign In
            </LoadingButton>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 5 }}>
              {'Copyright © Better Cargo '}
              {new Date().getFullYear()}.
            </Typography>
            {buildVersion ? (
              <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 0.5 }}>
                v{buildVersion}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Login;
